import type { wave } from '@round/api';
import type { CampaignStatus, Platform } from '@/types/creator';

export const DAY_MS = 24 * 60 * 60 * 1000;

export const PLATFORM_ICON: Record<wave.InvitePlatform, Platform> = {
    INSTAGRAM: 'Instagram',
    TIKTOK: 'TikTok',
    // No dedicated YouTube glyph in the ported set; fall back to the TikTok mark.
    YOUTUBE: 'TikTok',
};

export const PLATFORM_LABEL: Record<wave.InvitePlatform, string> = {
    INSTAGRAM: 'Instagram',
    TIKTOK: 'TikTok',
    YOUTUBE: 'YouTube',
};

export const getTimeLeft = (deadline: string | null): string => {
    if (!deadline) {
        return 'Open for submissions';
    }
    const deadlineMs = new Date(deadline).getTime();
    if (Number.isNaN(deadlineMs)) {
        return 'Open for submissions';
    }
    const diffMs = deadlineMs - Date.now();
    if (diffMs <= 0) {
        return 'Closed';
    }
    const days = Math.ceil(diffMs / DAY_MS);
    return `${days} day${days === 1 ? '' : 's'} left`;
};

export const formatDeadline = (deadline: string | null): string => {
    if (!deadline) {
        return '';
    }
    const deadlineMs = new Date(deadline).getTime();
    if (Number.isNaN(deadlineMs)) {
        return '';
    }
    return new Date(deadlineMs).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

// A live invite's status and response, present on both the list (CreatorInvite)
// and detail (Invite) payloads, is enough to place it in a lifecycle bucket.
type InviteLifecycleSource = {
    status: wave.InviteStatus;
    response: wave.InviteResponseChoice | null;
    submission_availability: wave.InviteSubmissionAvailability;
};

/**
 * Maps a real invite to the portal lifecycle buckets, including read-time campaign
 * availability so exhausted/ended invites do not still look actionable.
 * A decline is only ever recorded on `response`; a lapsed ("missed") invite and a
 * paid one are both terminal, so they read as completed.
 */
export const getInviteLifecycleStatus = ({
    status,
    response,
    submission_availability: submissionAvailability,
}: InviteLifecycleSource): CampaignStatus => {
    if (response === 'declined') {
        return 'declined';
    }
    if (status === 'paid' || status === 'missed') {
        return 'completed';
    }
    // A lapsed personal reservation is still actionable: the creator can re-reserve
    // while the campaign itself stays open.
    if (submissionAvailability !== 'open' && submissionAvailability !== 'reservation_expired') {
        return 'closed';
    }
    if (status === 'submitted' || response === 'accepted') {
        return 'active';
    }
    return 'invited';
};

type InviteOffer = {
    amount: string;
    detail: string;
};

/**
 * Builds the offer amount and supporting line without client-side FX. The backend
 * returns CPM values in the creator's selected display currency; fixed invites
 * retain their frozen offer currency.
 */
export const getInviteOffer = (invite: {
    campaign_type: wave.WaveCampaignType;
    offer_amount: string | null;
    max_earnable: string | null;
    max_payout: string | null;
    offer_currency_symbol: string;
    effective_max_posts: number | null;
}): InviteOffer => {
    const symbol = invite.offer_currency_symbol;

    if (invite.campaign_type === 'cpm') {
        const amount = invite.max_earnable === null ? 'Unlimited' : `${symbol}${invite.max_earnable}`;
        const rate = invite.offer_amount === null ? 'CPM rate unavailable' : `${symbol}${invite.offer_amount} CPM`;
        const perPostMaximum =
            invite.max_payout === null ? 'no per-post maximum' : `${symbol}${invite.max_payout} maximum per post`;
        return { amount, detail: `${rate} · ${perPostMaximum}` };
    }

    const amount = invite.offer_amount === null ? '' : `${symbol}${invite.offer_amount}`;
    const isMultiPost = invite.effective_max_posts !== null && invite.effective_max_posts > 1;
    const detail =
        invite.effective_max_posts === null
            ? 'per post · unlimited posts'
            : isMultiPost
              ? `per post · up to ${invite.effective_max_posts} posts`
              : 'per post';
    return { amount, detail };
};
